/**
 * Sort payers by clinic-scoped usage:
 *   - Items with `usageCount > 0` come first, ordered by count descending.
 *   - Items with `usageCount === 0` (or undefined) come last, in their
 *     original input order (stable — preserves Stedi's relevance ranking).
 *   - Ties within either group preserve input order.
 *
 * `Array.prototype.sort` is stable in modern JS engines, so a plain
 * comparator is enough — no manual index tracking required.
 */
export function sortPayersByUsage<T extends { usageCount?: number }>(
  payers: readonly T[],
): T[] {
  const usage = (p: T) => p.usageCount ?? 0
  return [...payers].sort((a, b) => {
    const ua = usage(a)
    const ub = usage(b)
    if (ua === 0 && ub === 0) return 0
    if (ua === 0) return 1
    if (ub === 0) return -1
    return ub - ua
  })
}

/**
 * Number of leading items to render bold — the top 3 payers with
 * `usageCount > 0`. Returns 0 when no payer has usage; capped at 3.
 */
export function boldedPayerCount(
  sortedPayers: readonly { usageCount?: number }[],
): number {
  let n = 0
  for (const p of sortedPayers) {
    if ((p.usageCount ?? 0) > 0 && n < 3) n++
    else break
  }
  return n
}
